import { useEffect, useRef, useState } from "react";
import { Pause, Play, Stop, BellSimple, BellSimpleSlash, CloudSlash, CloudArrowUp, GearSix, Lock } from "@phosphor-icons/react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { fmtClock } from "@/lib/format";
import { ThemeToggle } from "@/components/observer/ThemeToggle";
import { MicMeter } from "@/components/observer/MicMeter";
import { MicPicker } from "@/components/observer/MicPicker";

const NetBadge = ({ online, queued }) => {
  if (online && !queued) return null;
  return (
    <div className={`flex items-center gap-1.5 h-8 px-2 border font-mono text-[11px] ${online ? "border-[var(--o-border)] text-[var(--o-muted)]" : "border-[#EF4444]/50 text-[#EF4444]"}`} title={online ? "Audio chunks saved locally, uploading" : "Offline: audio is being saved locally and will upload when the connection returns"} data-testid="network-badge">
      {online ? <CloudArrowUp size={14} /> : <CloudSlash size={14} />}
      {online ? "" : "Offline"}{queued ? ` ${queued} queued` : ""}
    </div>
  );
};

const Indicator = ({ meta, capturing }) => {
  let label = "Idle";
  let color = "var(--o-muted3)";
  let live = false;
  if (meta.paused) label = "Paused";
  else if (meta.mode === "replay") {
    const st = meta.replay?.status;
    live = st === "running" || st === "preparing";
    label = st === "preparing" ? "Preparing" : st === "running" ? "Replaying" : st === "done" ? "Replay done" : st === "error" ? "Replay error" : "Replay";
    color = live ? "#F59E0B" : st === "error" ? "#EF4444" : "var(--o-muted3)";
  } else if (capturing) {
    live = true;
    label = "Listening";
    color = "#FF3B30";
  } else label = "Mic off";
  return (
    <div className="flex items-center gap-2.5" data-testid="listening-indicator">
      <span className="relative flex h-2.5 w-2.5">
        {live && <span className="listen-ring absolute inline-flex h-full w-full rounded-full" style={{ background: color }} />}
        <span className="relative inline-flex h-2.5 w-2.5 rounded-full" style={{ background: color }} />
      </span>
      <span className="font-mono text-xs uppercase tracking-[0.16em]" style={{ color: live ? "var(--o-fg)" : "var(--o-muted2)" }} data-testid="listening-indicator-label">{label}</span>
    </div>
  );
};

function useElapsed(meta) {
  const base = useRef({ v: 0, at: Date.now() });
  const [, tick] = useState(0);
  useEffect(() => {
    if (meta) base.current = { v: meta.elapsed, at: Date.now() };
  }, [meta]);
  useEffect(() => {
    const iv = setInterval(() => tick((x) => x + 1), 1000);
    return () => clearInterval(iv);
  }, []);
  if (!meta) return 0;
  const running = meta.mode === "live" && meta.status === "active";
  return running ? base.current.v + (Date.now() - base.current.at) / 1000 : meta.elapsed;
}

// Ticks once a second on its own, so the rest of the session page doesn't redraw with the clock.
const ElapsedStat = ({ meta }) => {
  const elapsed = useElapsed(meta);
  return <Stat label={meta.mode === "replay" ? "Rec. time" : "Elapsed"} value={fmtClock(elapsed)} testid="elapsed-time" />;
};

const Stat = ({ label, value, testid }) => (
  <div className="hidden md:flex flex-col leading-none">
    <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-[var(--o-muted3)]">{label}</span>
    <span className="font-mono text-sm mt-1" data-testid={testid}>{value}</span>
  </div>
);

const IntervalField = ({ value, onCommit }) => {
  const [v, setV] = useState(value);
  useEffect(() => setV(value), [value]);
  const commit = () => Number(v) !== Number(value) && onCommit(Number(v));
  return (
    <label className="flex items-center gap-1.5 border border-[var(--o-border)] px-2 h-8 focus-within:border-[var(--o-fg)] transition-colors" title="Minutes between background flag checks">
      <span className="font-mono text-[10px] text-[var(--o-muted3)] uppercase">Flag</span>
      <input type="number" min="0.5" step="0.5" value={v} onChange={(e) => setV(e.target.value)} onBlur={commit} onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()} className="w-10 bg-transparent font-mono text-xs outline-none" data-testid="topbar-flag-interval-input" />
      <span className="font-mono text-[10px] text-[var(--o-muted3)]">min</span>
    </label>
  );
};

const effortLabel = (lvl) => (lvl === "default" ? "Model default" : lvl[0].toUpperCase() + lvl.slice(1));
const selCls = "w-full h-9 bg-[var(--o-bg)] border border-[var(--o-border)] px-2 font-mono text-xs outline-none hover:border-[var(--o-border-strong)] focus:border-[var(--o-fg)]";

const Row = ({ label, hint, children }) => (
  <div className="space-y-1.5">
    <div className="font-mono text-[10px] uppercase tracking-[0.14em] text-[var(--o-muted2)]">{label}</div>
    {children}
    {hint && <div className="text-[11px] text-[var(--o-muted3)] leading-snug">{hint}</div>}
  </div>
);

// Settings people rarely touch mid-meeting live behind one button, so the bar shows only what matters live.
const SessionSettings = ({ meta, onSettings }) => {
  const s = meta.settings;
  const providers = meta.providers || {};
  const levels = providers[s.provider]?.effort_levels || ["default"];
  const voices = meta.voices || {};
  const anyVoice = Object.values(voices).some((v) => v.configured);
  const switchProvider = (provider) => {
    const next = providers[provider]?.effort_levels || ["default"];
    // Carry the effort over only if the new model supports it; otherwise the server rejects the switch.
    onSettings({ provider, reasoning_effort: next.includes(s.reasoning_effort) ? s.reasoning_effort : "default" });
  };
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button className="h-8 px-3 flex items-center gap-1.5 border border-[var(--o-border)] text-xs font-semibold hover:bg-[var(--o-surface)] transition-colors" data-testid="session-settings-button">
          <GearSix size={14} /> Settings
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 rounded-none border-[var(--o-border)] bg-[var(--o-bg2)] text-[var(--o-fg)] p-4 space-y-4" data-testid="session-settings">
        {meta.engines && meta.status === "active" && (
          <Row label="Transcription" hint={`${meta.engines[s.transcription_engine || "flash"]?.note || ""} Applies from the next chunk.`}>
            <select value={s.transcription_engine || "flash"} onChange={(e) => onSettings({ transcription_engine: e.target.value })} className={selCls} data-testid="engine-select">
              {Object.entries(meta.engines).map(([k, v]) => (
                <option key={k} value={k} disabled={!v.configured}>{v.label}{v.configured ? "" : " · no key"}</option>
              ))}
            </select>
          </Row>
        )}
        <Row label="Answers from">
          <div className="flex gap-2">
            <select value={s.provider} onChange={(e) => switchProvider(e.target.value)} className={selCls} data-testid="provider-select">
              <option value="anthropic" disabled={providers.anthropic ? !providers.anthropic.configured : false}>Anthropic</option>
              <option value="fireworks" disabled={providers.fireworks ? !providers.fireworks.configured : false}>
                Fireworks{providers.fireworks && !providers.fireworks.configured ? " · no key" : ""}
              </option>
            </select>
            {levels.length > 1 && (
              <select value={s.reasoning_effort || "default"} onChange={(e) => onSettings({ reasoning_effort: e.target.value })} className={selCls} title={providers[s.provider]?.effort_note} data-testid="effort-select">
                {levels.map((lvl) => <option key={lvl} value={lvl}>{effortLabel(lvl)}</option>)}
              </select>
            )}
          </div>
        </Row>
        {anyVoice && (
          <Row label="Read-aloud voice">
            <select value={s.tts_provider} onChange={(e) => onSettings({ tts_provider: e.target.value })} className={selCls} data-testid="voice-select">
              {Object.entries(voices).map(([k, v]) => (
                <option key={k} value={k} disabled={!v.configured}>{v.label}{v.configured ? "" : " · no key"}</option>
              ))}
            </select>
          </Row>
        )}
        <Row label="Background flags">
          <div className="flex items-center gap-2">
            <IntervalField value={s.flag_interval_min} onCommit={(v) => onSettings({ flag_interval_min: v })} />
            <button onClick={() => onSettings({ chime: !s.chime })} className={`h-8 px-2.5 flex items-center gap-1.5 border border-[var(--o-border)] text-xs hover:bg-[var(--o-surface)] transition-colors ${s.chime ? "text-[var(--o-fg)]" : "text-[var(--o-muted3)]"}`} data-testid="chime-toggle">
              {s.chime ? <BellSimple size={14} weight="fill" /> : <BellSimpleSlash size={14} />} Chime {s.chime ? "on" : "off"}
            </button>
          </div>
        </Row>
      </PopoverContent>
    </Popover>
  );
};

export const TopBar = ({ meta, meetingStream = null, capturing, stream, deadMic = false, onSettings, onPause, onEnd, ending = false, online = true, queued = 0, micId = "", onMic }) => {
  const meetingConnected = !!meetingStream;
  const chunkLabel = meta.mode === "replay" && meta.replay?.chunks_total ? `${meta.chunks_done + meta.chunks_failed}/${meta.replay.chunks_total}` : `${meta.chunks_done + meta.chunks_failed}`;
  return (
    <header className="min-h-16 flex flex-wrap items-center justify-between gap-3 px-4 sm:px-6 py-2 border-b border-[var(--o-border)] bg-[var(--o-bg)] shrink-0 relative z-20" data-testid="session-topbar">
      <div className="flex flex-wrap items-center gap-x-5 gap-y-2 min-w-0 max-w-full">
        <div className="min-w-0 max-w-full">
          <div className="flex items-center gap-2">
            <h1 className="font-display text-lg sm:text-xl font-extrabold tracking-tight truncate max-w-[60vw] md:max-w-[40vw]" data-testid="session-title">{meta.title}</h1>
            {meta.private && (
              <span className="inline-flex items-center gap-1 font-mono text-[10px] border border-[var(--o-warn)] text-[var(--o-warn)] px-1.5 py-0.5" title={`Private: nothing from this meeting is saved by Observer. ${meta.audio_ram_only ? "Audio is kept in memory, not on disk, on this server." : "Audio is written to disk for a moment and deleted straight after processing."} The AI services still receive it.`} data-testid="session-private-badge">
                <Lock size={10} weight="fill" aria-hidden="true" /> PRIVATE · NOT SAVED
              </span>
            )}
            {meta.audio?.source === "online" && (
              <span className={`font-mono text-[10px] border px-1.5 py-0.5 ${meetingConnected ? "border-[#10B981]/40 text-[#10B981]" : "border-[#F59E0B]/40 text-[#F59E0B]"}`} title={meetingConnected ? "Recording your mic and the call's audio" : "Call audio not connected: only your mic is recorded"} data-testid="session-online-badge">
                {meetingConnected ? "ONLINE · CALL AUDIO" : "ONLINE · MIC ONLY"}
              </span>
            )}
            {meta.mode === "replay" && <span className="font-mono text-[10px] border border-[#F59E0B]/40 text-[#F59E0B] px-1.5 py-0.5" data-testid="session-mode-badge">REPLAY</span>}
          </div>
        </div>
        <Indicator meta={meta} capturing={capturing} />
        <MicMeter stream={stream} dead={deadMic} />
        {meta.mode === "live" && meta.status === "active" && onMic && <MicPicker value={micId} onChange={onMic} stream={stream} online={meta.audio?.source === "online"} />}
        {meta.audio?.source === "online" && capturing && <MicMeter stream={meetingStream} kind="call" />}
      </div>
      <div className="flex flex-wrap items-center gap-4 sm:gap-6 max-w-full">
        <Stat label="Chunks" value={<>{chunkLabel}{meta.chunks_fallback ? <span className="text-[#F59E0B]" title="Chunks Gemini failed that Sarvam transcribed (no speaker names)"> · {meta.chunks_fallback}↺</span> : null}{meta.chunks_failed ? <span className="text-[#EF4444]"> · {meta.chunks_failed}✕</span> : null}</>} testid="chunk-counter" />
        <ElapsedStat meta={meta} />
        <div className="flex flex-wrap items-center gap-2">
          <NetBadge online={online} queued={queued} />
          <SessionSettings meta={meta} onSettings={onSettings} />
          <button onClick={onPause} className={`h-8 px-3 flex items-center gap-1.5 border text-xs font-semibold transition-colors ${meta.paused ? "border-[#F59E0B] text-[#F59E0B] hover:bg-[#F59E0B]/10" : "border-[var(--o-border)] hover:bg-[var(--o-surface)]"}`} data-testid="pause-button">
            {meta.paused ? <Play size={13} weight="fill" /> : <Pause size={13} weight="fill" />} {meta.paused ? "Resume" : "Pause"}
          </button>
          <button onClick={onEnd} disabled={ending} className="h-8 px-3 flex items-center gap-1.5 bg-[var(--o-fg)] text-[var(--o-bg)] text-xs font-semibold hover:opacity-90 disabled:opacity-60 transition-colors" data-testid="end-session-button">
            <Stop size={13} weight="fill" /> {ending ? "Ending…" : "End"}
          </button>
          <ThemeToggle />
        </div>
      </div>
    </header>
  );
};
